<template>
  <div>
    <es-loader
      v-if="loading"
      style="margin: 4.5rem"
    />
    <es-exception
      v-else-if="loadingError"
      exceptionMessage="There was an issue. Please try again or contact your administrator."
      margin="1rem"
      style="padding: 5rem"
    />
    <es-exception
      v-else-if="locationNotFound"
      exceptionMessage="Location not found."
      margin="1rem"
      style="padding: 5rem"
    />
    <div v-else>
      <div
        class="document"
        :class="{ 'margin--top': $route.query.retired }"
      >
        <div
          class="header"
          style="display: flex"
        >
          <EsHeader>
            <template #header>
              <div class="header-title">
                Location Information Details
                ({{ personLocation.personTypeName }})
              </div>
            </template>
          </EsHeader>
          <div class="icon-right">
            <router-link
              v-if="this.activeModule.name === 'personId'"
              tabindex="0"
              title="View details"
              :to="{
                name: 'PidAssociatedRecordDetails',
                query: {
                  id: $route.query.id,
                  retired: isRetired,
                  recordRetired: isRecordRetired,
                },
              }"
            >
              <font-awesome-icon
                :icon="['far', 'angle-double-left']"
                size="2x"
                title="View Details"
              />
            </router-link>
            <router-link
              v-if="this.activeModule.name === 'directMatch'"
              tabindex="0"
              title="View details"
              :to="{
                name: 'DmAssociatedRecordDetails',
                query: {
                  id: $route.query.id,
                  retired: isRetired,
                  recordRetired: isRecordRetired,
                },
              }"
            >
              <font-awesome-icon
                :icon="['far', 'angle-double-left']"
                size="2x"
                title="View Details"
              />
            </router-link>
          </div>
        </div>
        <div style="padding: 1rem 0">
          <div class="columns">
            <div
              class="column district-name"
              style="border-right: 1px solid #dee2e6 !important;"
            >
              {{ personLocation.districtName || personLocation.district.location.name }}
              <span
                class="district-code"
                style="padding-left: 0.25rem"
              >
                ({{ personLocation.districtCode }})
              </span>
              <div class="school-name">
                {{ personLocation.schoolName || personLocation.school.location.name }}
                <span
                  class="school-code"
                  style="padding-left: 0.25rem;
                    font-style: italic"
                >
                  ({{ personLocation.schoolCode }})
                </span>
              </div>
            </div>
            <div
              class="column"
              style="border-right: 1px solid #dee2e6 !important"
            >
              <div style="font-size: 16px; font-weight: 700">
                {{ personLocation.lastUpdateDate | formatDateMMDDYYYY }}
              </div>
              <div class="school-code">
                Last Updated
              </div>
            </div>
            <div class="column">
              <div style="font-size: 16px; font-weight: 700">
                {{ personLocation.personTypeName }}
              </div>
              <div class="school-code">
                Person Type
              </div>
            </div>
          </div>
        </div>
        <es-form style="margin-right: 0rem;">
          <template #es-form>
            <div
              class="content"
              style="padding: 1rem 0"
            >
              <div class="columns">
                <div class="column">
                  <div
                    v-if="fieldEnabled('SCHOOL_YEAR')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SCHOOL_YEAR') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              type="text"
                              :value="personLocation.schoolYear"
                              :aria-label="fieldLabel('SCHOOL_YEAR')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('DISTRICT_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('DISTRICT_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <div
                              class="input is-static is-readonly"
                              v-html="districtNameAndCode"
                            >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SCHOOL_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SCHOOL_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <div
                              class="input is-static is-readonly"
                              v-html="schoolNameAndCode"
                            >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('RES_DISTRICT_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('RES_DISTRICT_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <div
                              class="input is-static is-readonly"
                              v-html="resDistrictNameAndCode"
                            >
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('LOCAL_STUDENT_ID')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('LOCAL_STUDENT_ID') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="personLocation.localId"
                              :aria-label="fieldLabel('LOCAL_STUDENT_ID')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SOURCE_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SOURCE_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="mapSource(personLocation.sourceCode)"
                              :aria-label="fieldLabel('SOURCE_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ALT_STUDENT_ID')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ALT_STUDENT_ID') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="personLocation.altPersonId"
                              :aria-label="fieldLabel('ALT_STUDENT_ID')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ALT_SOURCE_SIS_CODE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ALT_SOURCE_SIS_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="mapSource(personLocation.altSourceCode)"
                              :aria-label="fieldLabel('ALT_SOURCE_SIS_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ACTIVE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ACTIVE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="activeStatus"
                              :aria-label="fieldLabel('ACTIVE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SOURCE_REFRESH_DATE')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SOURCE_REFRESH_DATE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="personLocation.sourceRefreshDate | formatDatePreserveZone"
                              :aria-label="fieldLabel('SOURCE_REFRESH_DATE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('LOCATION_COMMENT')"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('LOCATION_COMMENT') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <div class="textarea-wrapper">
                              <textarea
                                class="disabled-textarea"
                                maxlength="100"
                                rows="3"
                                style="width: 100%;
                                  resize: none;
                                  padding-left: 10px;
                                  overflow: auto;"
                                :value="personLocation.locationComment"
                                :aria-label="fieldLabel('LOCATION_COMMENT')"
                                disabled
                              ></textarea>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="column">
                  <div
                    v-if="fieldEnabled('GRADE_CODE')
                      && personLocation.personTypeCategory === 'STUDENT'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('GRADE_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="mapGrades(personLocation.gradeCode)"
                              :aria-label="fieldLabel('GRADE_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('HIRE_DATE')
                      && personLocation.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('HIRE_DATE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="personLocation.hireDate | formatDateMMDDYYYY"
                              :aria-label="fieldLabel('HIRE_DATE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('SALARY')
                      && personLocation.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('SALARY') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="personLocation.salary"
                              :aria-label="fieldLabel('SALARY')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_CLASS_CODE')
                      && personLocation.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_CLASS_CODE') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="mapJobClassCode(personLocation.jobClassCode)"
                              :aria-label="fieldLabel('JOB_CLASS_CODE')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_ASSIGNMENT_1')
                      && personLocation.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_ASSIGNMENT_1') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="mapJobAssignments(personLocation.jobAssignment1)"
                              :aria-label="fieldLabel('JOB_ASSIGNMENT_1')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_ASSIGNMENT_2')
                      && personLocation.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_ASSIGNMENT_2') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="mapJobAssignments(personLocation.jobAssignment2)"
                              :aria-label="fieldLabel('JOB_ASSIGNMENT_2')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('JOB_ASSIGNMENT_3')
                      && personLocation.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('JOB_ASSIGNMENT_3') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="mapJobAssignments(personLocation.jobAssignment3)"
                              :aria-label="fieldLabel('JOB_ASSIGNMENT_3')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div
                    v-if="fieldEnabled('ITINERANT_FLAG')
                      && personLocation.personTypeCategory === 'EMPLOYEE'"
                    class="row"
                  >
                    <div class="field is-horizontal">
                      <div class="field-label is-normal">
                        <label class="label">
                          {{ fieldLabel('ITINERANT_FLAG') }}
                        </label>
                      </div>
                      <div class="field-body">
                        <div class="field">
                          <div class="control field-label is-normal">
                            <input
                              class="input is-static"
                              :value="personLocation.itinerantFlag
                                ? mapItinerantFlag(personLocation.itinerantFlag) : null"
                              :aria-label="fieldLabel('ITINERANT_FLAG')"
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </template>
        </es-form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { mapGetters } from 'vuex';
import { library } from '@fortawesome/fontawesome-svg-core';
import {
  faAngleDoubleLeft,
  faEdit,
} from '@fortawesome/pro-regular-svg-icons';
import EsException from '@/components/EsException.vue';
import EsForm from '@/components/EsForm.vue';
import EsHeader from '@/components/EsHeader.vue';
import EsLoader from '@/components/EsLoader.vue';
import DateUtils from '@/mixins/DateUtils';
import FieldLabels from '@/mixins/FieldLabels';
import NotificationManager from '@/mixins/NotificationManager';
import PermissionManager from '@/mixins/PermissionManager';

library.add(
  faAngleDoubleLeft,
  faEdit,
);

export default {
  mixins: [
    DateUtils,
    FieldLabels,
    NotificationManager,
    PermissionManager,
  ],
  components: {
    EsException,
    EsForm,
    EsHeader,
    EsLoader,
  },
  props: {
    isRetired: {
      type: Boolean,
    },
    isRecordRetired: {
      type: Boolean,
    },
    person: {
      type: Object,
      required: true,
    },
  },
  data() {
    const temp = {
      activeStatusOptions: [],
      personLocation: {},
      loading: false,
      loadingError: false,
      locationNotFound: false,
      resDistrictNameAndCode: null,
    };
    return temp;
  },
  created() {
    this.onLoad();
    this.setActiveStatusOptions();
  },
  computed: {
    ...mapGetters({
      activeModule: 'appModules/getActiveModule',
    }),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeGrades',
      'sources',
      'activeJobAssignments',
      'activeItinerantFlags',
      'activeTitles',
      'pidSchoolYearsDesc',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldEnabledForPersonType',
      'getFieldRequiredForPersonType',
    ]),
    activeStatus() {
      if (this.personLocation.active === true) {
        return 'Active';
      }
      if (this.personLocation.active === false) {
        return 'Inactive';
      }
      return null;
    },
    districtNameAndCode() {
      return this.formatNameAndCode(
        this.personLocation.districtName, this.personLocation.districtCode,
      );
    },
    schoolNameAndCode() {
      return this.formatNameAndCode(this.personLocation.schoolName, this.personLocation.schoolCode);
    },
  },
  methods: {
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.personLocation.personType);
    },
    fieldEnabled(code) {
      return this.getFieldEnabledForPersonType(code, 'ASSIGN', this.personLocation.personType);
    },
    formatNameAndCode(name, code) {
      return `${name} (${code})`.replace(/(\(\d+\))$/, '&nbsp;<i style="color: #737373; font-size: 0.75rem">$1</i>');
    },
    async getPersonLocations() {
      const { id, lastTranSerialNumber } = this.$route.query;

      try {
        const baseUrl = this.isRecordRetired ? `retired-person/${id}/location` : `person/${id}/location`;
        const { data } = await axios.get(`${baseUrl}`);
        return data;
      } catch (error) {
        console.error('Primary request failed:', error);

        if (error.response && error.response.status !== 200) {
          try {
            const baseUrl = this.isRecordRetired ? `retired-person/${id}/locationhistory/${lastTranSerialNumber}` : `person/${id}/locationhistory/${lastTranSerialNumber}`;
            const { data } = await axios.get(`${baseUrl}`);
            return data;
          } catch (secondError) {
            console.error('Second request failed:', secondError);
            throw secondError;
          }
        }
        throw error;
      }
    },
    async getResDistrictName() {
      if (!this.personLocation.resDistrictCode) {
        return this.resDistrictNameAndCode;
      }
      try {
        const response = await axios.get(`/districts/${this.personLocation.resDistrictCode}?allDistricts=true`);
        const districtName = response.data.location.name;
        const formattedCode = `&nbsp;<i style="color: #737373; font-size: 0.75rem">(${this.personLocation.resDistrictCode})</i>`;
        this.resDistrictNameAndCode = `${districtName} ${formattedCode}`;
      } catch (error) {
        console.error(error);
      }
      return this.resDistrictNameAndCode;
    },
    async handleFallbackRequest(recordId) {
      try {
        const url = (this.$route.query.retired === true || this.$route.query.retired === 'true')
          ? `retired-person/${this.$route.params.id}/location`
          : `person/${this.$route.params.id}/location`;

        const { data } = await axios.get(url);

        const filteredData = data.filter(
          item => String(item.lastTranSerialNumber) === String(recordId),
        );

        return { data: filteredData[0] || {} };
      } catch (fallbackError) {
        console.error('Fallback request failed:', fallbackError);
        throw fallbackError; // Re-throw the error for further handling
      }
    },
    mapDescription(collection, code) {
      const item = collection.find(i => i.code === code);
      return item ? item.description : '\u00A0';
    },
    mapGrades(gradeCode) {
      return this.mapDescription(this.activeGrades, gradeCode);
    },
    mapItinerantFlag(flag) {
      return this.mapDescription(this.activeItinerantFlags, flag);
    },
    mapJobClassCode(code) {
      return this.mapDescription(this.activeTitles, code);
    },
    mapJobAssignments(code) {
      return this.mapDescription(this.activeJobAssignments, code);
    },
    mapSource(code) {
      return this.mapDescription(this.sources, code);
    },
    async onLoad() {
      this.loading = true;
      try {
        const locationsResponse = await Promise.all([
          this.getPersonLocations(),
        ]);
        this.personLocation = locationsResponse
          .flatMap(group => group) // Flatten one level
          .find(
            location => location.personId === String(this.$route.query.id)
            && location.lastTranSerialNumber === Number(this.$route.query.lastTranSerialNumber),
          );
        if (Object.keys(locationsResponse).length === 0) {
          this.locationNotFound = true;
        } else {
          this.getResDistrictName();
        }
      } catch (error) {
        console.error(error);
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    setActiveStatusOptions() {
      this.activeStatusOptions = [
        { code: false, description: 'Inactive' },
        { code: true, description: 'Active' },
      ];
    },
  },
};
</script>

<style lang="sass" scoped>
.border-red
  border-color: red
.control
  padding-left: 30px
.columns
  display: flex
  flex-wrap: wrap
  margin: 0
.column
  padding: 0
  flex: 1 1 auto
.control
  padding-left: 0 !important
.disabled-textarea
  color: black
  resize: none
  overflow: auto
  padding-left: 0
  padding-right: 0
  cursor: default
  background-color: transparent
  border: none
  outline: none
.district-code
  font-size: 14px
  color: #6C757D
  font-style: italic
  font-weight: 400
.district-name
  color: #212529
  font-size: 18.4px
  font-weight: 700
  text-align: left
  padding-left: 2rem
.icon-right
  padding-right: 1.5rem
  padding-top: 1.5rem
.is-static
  pointer-events: none
.label
  width: 200px
.margin--top
  margin-top: 1rem
.school-code
  font-size: 12px
  color: #6C757D
  font-weight: 400
.school-name
  color: #212529
  font-size: 14px
  font-weight: 700
.textarea-wrapper
  position: relative
  overflow: auto
.textarea-wrapper textarea:focus
  outline: none
</style>
